import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSleep } from '../../contexts/SleepContext';
import { isIOS } from '../../utils/platform';
import { getSetting } from '../../db/sleepDB';
import {
  registerReminderTapListener,
  scheduleMorningReminder,
  getReminderPermissionState,
  DEFAULT_REMINDER_TIME,
} from '../../services/reminders';

// Renders nothing. iOS native only — owns the two app-lifetime reminder jobs:
//
// 1. Deep link: registers the LocalNotifications actionPerformed listener ONCE
//    (empty-deps effect + module-level guard in the service) and navigates to
//    /log when the morning reminder is tapped. navigate lives in a ref so the
//    single listener always sees the latest router without re-binding.
// 2. Schedule re-assert: on app open (and whenever the streak count changes,
//    so the notification copy stays honest), if reminders are enabled AND the
//    OS permission is granted, cancel+reschedule the known notification id.
//    Idempotent by design; never prompts for permission here.
export default function ReminderManager() {
  const navigate = useNavigate();
  const { streak } = useSleep();
  const streakCount = streak.current;

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isIOS()) return;
    registerReminderTapListener((route) => navigateRef.current(route || '/log'));
  }, []); // EMPTY deps — bind once for the app lifetime

  const lastAssertedRef = useRef(null);
  useEffect(() => {
    if (!isIOS()) return;
    (async () => {
      try {
        const enabled = await getSetting('reminderEnabled');
        if (!enabled) return;
        const perm = await getReminderPermissionState();
        if (perm !== 'granted') return; // denied at OS level — Settings shows the honest state
        const time = (await getSetting('reminderTime')) || DEFAULT_REMINDER_TIME;
        const key = `${time}|${streakCount}`;
        if (lastAssertedRef.current === key) return;
        lastAssertedRef.current = key;
        await scheduleMorningReminder({ time, streakCount });
      } catch (err) {
        // Background job — never surface a toast on app open.
        console.warn('Reminder re-assert failed:', err);
      }
    })();
  }, [streakCount]);

  return null;
}
